import { z } from 'zod';

const stringSchema = z.string();

const numberSchema = z.union([z.number(), z.nan()]);

const booleanSchema = z.boolean();

const bigintSchema = z.bigint();

const symbolSchema = z.symbol();

const functionSchema = z.function();

export type JsonPrimitive = string | number | boolean | null;

export interface JsonObject {
  [key: string]: JsonValue | undefined;
}

export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];

export function isStringValue(value: unknown): value is string {
  return stringSchema.safeParse(value).success;
}

export function isNumberValue(value: unknown): value is number {
  return numberSchema.safeParse(value).success;
}

export function isBooleanValue(value: unknown): value is boolean {
  return booleanSchema.safeParse(value).success;
}

export function isBigIntValue(value: unknown): value is bigint {
  return bigintSchema.safeParse(value).success;
}

export function isSymbolValue(value: unknown): value is symbol {
  return symbolSchema.safeParse(value).success;
}

export function isFunctionValue(value: unknown): value is (...args: never[]) => unknown {
  return functionSchema.safeParse(value).success;
}

export function isObjectValue(value: unknown): value is object {
  return value !== null && Object(value) === value && !functionSchema.safeParse(value).success;
}

export function hasRuntimeObjectType(value: unknown): value is object {
  return isObjectValue(value);
}

export type RuntimeTypeName =
  | 'bigint'
  | 'boolean'
  | 'function'
  | 'number'
  | 'object'
  | 'string'
  | 'symbol'
  | 'undefined';

export function runtimeTypeName(value: unknown): RuntimeTypeName {
  if (value === undefined) return 'undefined';

  if (isStringValue(value)) return 'string';

  if (isNumberValue(value)) return 'number';

  if (isBooleanValue(value)) return 'boolean';

  if (isBigIntValue(value)) return 'bigint';

  if (isSymbolValue(value)) return 'symbol';

  if (isFunctionValue(value)) return 'function';

  return 'object';
}
